import type {
  DefenseInningDocument,
  PlayerListItem,
} from "../shared/types/contracts";

export const FIELD_SPOTS: Record<string, readonly [number, number]> = {
  C: [200, 336],
  P: [200, 248],
  "1B": [288, 232],
  "2B": [248, 182],
  SS: [152, 182],
  "3B": [112, 232],
  LF: [72, 108],
  LC: [156, 68],
  RC: [244, 68],
  RF: [328, 108],
};

export interface SitScheduleRow {
  player: PlayerListItem;
  innings: number[];
  label: string;
}

export interface DefenseFieldLabel {
  position: string;
  x: number;
  y: number;
  playerId: string | null;
  playerName: string;
  isOop: boolean;
}

export function playerName(
  players: PlayerListItem[],
  playerId: string,
): string {
  return players.find((player) => player.id === playerId)?.name || "—";
}

export function isOutOfPosition(
  position: string,
  playerId: string | null | undefined,
  preferences: Record<string, string[]>,
): boolean {
  return Boolean(playerId && !(preferences[playerId] ?? []).includes(position));
}

export function defenseCellLabel(
  inning: DefenseInningDocument,
  position: string,
  players: PlayerListItem[],
  preferences: Record<string, string[]>,
): string {
  const playerId = inning.assignment[position];
  if (!playerId) return "—";
  return `${playerName(players, playerId)}${
    isOutOfPosition(position, playerId, preferences) ? " ⚠" : ""
  }`;
}

export function buildOopNotes(
  defense: DefenseInningDocument[],
  players: PlayerListItem[],
): string[] {
  return defense.flatMap((inning, inningIndex) =>
    (inning.oop ?? []).map((playerId) => {
      const position = Object.keys(inning.assignment).find(
        (key) => inning.assignment[key] === playerId,
      );
      return `Inning ${inningIndex + 1}: ${playerName(
        players,
        playerId,
      )} at ${position} (not a listed position)`;
    }),
  );
}

export function buildDefenseTextGrid(
  defense: DefenseInningDocument[],
  players: PlayerListItem[],
  preferences: Record<string, string[]>,
  positions: readonly string[],
  inningCount: number,
): string {
  const header = [
    "POS",
    ...Array.from({ length: inningCount }, (_, index) => `I${index + 1}`),
  ].join("\t");
  const rows = positions.map((position) =>
    [
      position,
      ...defense.map((inning) =>
        defenseCellLabel(inning, position, players, preferences),
      ),
    ].join("\t"),
  );
  const sits = [
    "SIT",
    ...defense.map((inning) =>
      inning.sitters.map((id) => playerName(players, id)).join("/"),
    ),
  ].join("\t");
  return [header, ...rows, sits, "", ...buildOopNotes(defense, players)].join(
    "\n",
  );
}

export function buildSitSchedule(
  activePlayers: PlayerListItem[],
  defense: DefenseInningDocument[],
  gamePitcherId: string | null,
): SitScheduleRow[] {
  const schedule = Object.fromEntries(
    activePlayers.map((player) => [player.id, [] as number[]]),
  );
  defense.forEach((inning, inningIndex) => {
    inning.sitters.forEach((playerId) => {
      schedule[playerId]?.push(inningIndex + 1);
    });
  });
  return activePlayers.map((player) => {
    const innings = schedule[player.id] ?? [];
    return {
      player,
      innings,
      label:
        innings.length > 0
          ? `inn ${innings.join(", ")}`
          : player.id === gamePitcherId
            ? "P all 9"
            : "never",
    };
  });
}

export function buildDefenseFieldLabels(
  inning: DefenseInningDocument,
  players: PlayerListItem[],
  preferences: Record<string, string[]>,
  positions: readonly string[],
): DefenseFieldLabel[] {
  return positions.map((position) => {
    const [x, y] = FIELD_SPOTS[position]!;
    const playerId = inning.assignment[position] ?? null;
    return {
      position,
      x,
      y,
      playerId,
      playerName: playerId ? playerName(players, playerId) : "—",
      isOop: isOutOfPosition(position, playerId, preferences),
    };
  });
}
